import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, TextField, Select, MenuItem,
  FormControl, Switch, Chip, IconButton, Button,
  Menu, MenuList,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TuneIcon from '@mui/icons-material/Tune';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { SectionShell, DashedAddButton, fieldSx } from './ConfigurationFormShared';

// ─── constants ────────────────────────────────────────────────────────────────
const AREA_KEYWORDS = ['area', 'sqft', 'sq.ft', 'carpet', 'builtup', 'built-up', 'built_up', 'super'];

const AREA_UNITS = [
  { value: 'sqft',   label: 'sq.ft'    },
  { value: 'sqmt',   label: 'sq.mt'    },
  { value: 'sqyard', label: 'sq.yards' },
  { value: 'acres',  label: 'Acres'    },
  { value: 'gaj',    label: 'Gaj'      },
];

function isAreaField(field) {
  if (field.type !== 'number') return false;
  const h = `${field.label} ${field.key ?? ''}`.toLowerCase();
  return AREA_KEYWORDS.some((kw) => h.includes(kw));
}

// ─── Type badge ───────────────────────────────────────────────────────────────
const TYPE_COLORS = {
  select:  { bg: 'rgba(124,58,237,0.09)',  color: '#7c3aed' },
  boolean: { bg: 'rgba(14,165,233,0.09)',  color: '#0284c7' },
  number:  { bg: 'rgba(21,101,192,0.09)',  color: '#1565c0' },
  text:    { bg: 'rgba(100,116,139,0.09)', color: '#475569' },
};

const TypeBadge = ({ type }) => {
  const c = TYPE_COLORS[type] ?? TYPE_COLORS.text;
  return (
    <Chip
      label={type === 'bhk' ? 'select' : type}
      size="small"
      sx={{ height: 17, fontSize: '0.6rem', fontWeight: 700, bgcolor: c.bg, color: c.color, flexShrink: 0 }}
    />
  );
};

// ─── BHK pill selector (single select) ───────────────────────────────────────
const BhkSelector = ({ options = [], value, onChange }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, flex: 1 }}>
    {options.map((opt) => {
      const v = typeof opt === 'string' ? opt : opt.value;
      const l = typeof opt === 'string' ? opt : opt.label;
      const selected = value === v;
      return (
        <Button
          key={v}
          size="small"
          onClick={() => onChange(selected ? null : v)}
          sx={{
            height: 28, px: 1.25, minWidth: 0,
            fontSize: 12, fontWeight: 600,
            borderRadius: '6px',
            border: `1.5px solid ${selected ? '#1565c0' : '#E4E7EC'}`,
            bgcolor: selected ? 'rgba(21,101,192,0.10)' : '#fff',
            color: selected ? '#1565c0' : '#374151',
            textTransform: 'none',
            '&:hover': {
              border: '1.5px solid #1565c0',
              bgcolor: selected ? 'rgba(21,101,192,0.14)' : 'rgba(21,101,192,0.04)',
              color: '#1565c0',
            },
            transition: 'all .15s',
          }}
        >
          {l}
        </Button>
      );
    })}
  </Box>
);

// ─── Value input per field type ───────────────────────────────────────────────
const FieldValueInput = ({ field, value, unit, onValueChange, onUnitChange }) => {
  const { type, options = [], placeholder } = field;
  const isArea = isAreaField(field);

  if (type === 'bhk') {
    return <BhkSelector options={field.options ?? []} value={value} onChange={onValueChange} />;
  }

  if (type === 'boolean') {
    return (
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          border: '1px solid #E4E7EC', borderRadius: '7px',
          px: 1.5, height: 32, bgcolor: '#fff', flex: 1,
        }}
      >
        <Typography sx={{ fontSize: 12, color: '#6B7280' }}>{value ? 'Yes' : 'No'}</Typography>
        <Switch
          size="small"
          checked={!!value}
          onChange={(e) => onValueChange(e.target.checked)}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: '#2D6A4F' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#2D6A4F' },
          }}
        />
      </Box>
    );
  }

  if (type === 'select') {
    return (
      <FormControl size="small" sx={{ ...fieldSx, flex: 1 }}>
        <Select
          value={value ?? ''}
          onChange={(e) => onValueChange(e.target.value || null)}
          displayEmpty
          renderValue={(v) =>
            !v ? <span style={{ color: '#9CA3AF', fontSize: 12 }}>Select…</span>
               : (options.find((o) => o.value === v)?.label ?? v)
          }
        >
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value} sx={{ fontSize: 12.5 }}>{o.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  // number / text
  return (
    <Box sx={{ display: 'flex', gap: 0.75, flex: 1, minWidth: 0 }}>
      <TextField
        size="small" fullWidth
        type={type === 'number' ? 'number' : 'text'}
        placeholder={placeholder || (type === 'number' ? '0' : 'Enter value…')}
        value={value ?? ''}
        onChange={(e) =>
          onValueChange(e.target.value === '' ? null : type === 'number' ? Number(e.target.value) : e.target.value)
        }
        inputProps={{ min: 0, step: 'any' }}
        sx={fieldSx}
      />
      {isArea && (
        <FormControl size="small" sx={{ ...fieldSx, flexShrink: 0, minWidth: 88 }}>
          <Select
            value={unit ?? 'sqft'}
            onChange={(e) => onUnitChange(e.target.value)}
            renderValue={(v) => AREA_UNITS.find((u) => u.value === v)?.label ?? v}
          >
            {AREA_UNITS.map((u) => (
              <MenuItem key={u.value} value={u.value} sx={{ fontSize: 12.5 }}>{u.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );
};

// ─── Single added row ─────────────────────────────────────────────────────────
const AddedFieldRow = ({ field, value, unit, onValueChange, onUnitChange, onDelete }) => {
  const isFilled = value !== undefined && value !== null && value !== '' && value !== false;
  const isBhk = field.type === 'bhk';

  return (
    <Box
      sx={{
        border: `1px solid ${isFilled ? 'rgba(46,125,50,0.28)' : '#E4E7EC'}`,
        borderRadius: '8px', px: 1.25, py: 0.75,
        bgcolor: isFilled ? 'rgba(46,125,50,0.025)' : '#fff',
        transition: 'border-color 0.15s, background 0.15s',
        // BHK pills wrap to next line
        display: 'flex',
        flexDirection: isBhk ? 'column' : 'row',
        alignItems: isBhk ? 'flex-start' : 'center',
        gap: isBhk ? 0.75 : 1,
      }}
    >
      {/* Field name + delete (always top row) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
        <Box
          sx={{
            flex: '0 0 auto', maxWidth: 158, minWidth: 60,
            px: 1, py: 0.3,
            bgcolor: 'rgba(21,101,192,0.07)', borderRadius: '5px',
            display: 'flex', alignItems: 'center', gap: 0.5,
          }}
        >
          {field.required && <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#DC2626', flexShrink: 0 }} />}
          <Typography
            sx={{ fontSize: 11.5, fontWeight: 600, color: '#1565c0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={field.label}
          >
            {field.label}
          </Typography>
        </Box>

        {!isBhk && (
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
            <FieldValueInput
              field={field} value={value} unit={unit}
              onValueChange={onValueChange} onUnitChange={onUnitChange}
            />
          </Box>
        )}

        <IconButton
          size="small" onClick={onDelete}
          sx={{ color: '#9CA3AF', p: 0.4, flexShrink: 0, ml: 'auto', '&:hover': { color: '#DC2626', bgcolor: 'rgba(220,38,38,0.06)' } }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      {/* BHK pills row */}
      {isBhk && (
        <Box sx={{ pl: 0.5, width: '100%' }}>
          <FieldValueInput
            field={field} value={value} unit={unit}
            onValueChange={onValueChange} onUnitChange={onUnitChange}
          />
        </Box>
      )}
    </Box>
  );
};

// ─── Field picker dropdown (MenuItem so list is visible and focusable in Menu) ──
const FieldPicker = ({ fields, addedKeys, onPick }) => {
  const available = fields.filter((f) => !addedKeys.includes(f.key));
  if (!available.length) {
    return (
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>All fields have been added</Typography>
      </Box>
    );
  }
  return (
    <>
      {available.map((field) => (
        <MenuItem
          key={field.key}
          onClick={() => onPick(field.key)}
          dense
          sx={{
            py: 0.9, px: 1.5,
            fontSize: 12.5, color: '#111827',
            '&:hover': { bgcolor: '#F5F7FF' },
          }}
        >
          <Typography sx={{ flex: 1, minWidth: 0, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {field.label}
            {field.required && <span style={{ color: '#DC2626', marginLeft: 3, fontSize: 11 }}>*</span>}
          </Typography>
          {isAreaField(field) && (
            <Chip label="area" size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, bgcolor: 'rgba(0,105,92,0.09)', color: '#00695c', flexShrink: 0, ml: 0.5 }} />
          )}
          <KeyboardArrowRightIcon sx={{ fontSize: 14, color: '#D1D5DB', flexShrink: 0 }} />
        </MenuItem>
      ))}
    </>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
/**
 * ConfigurationDetailsFields — unit-level fields from config_ui_schema (BHK, areas, etc.)
 *
 * Props:
 *  sections   Array<{ key, label, order, fields[] }>
 *  value      { [fieldKey]: { value: any, unit?: string } }   controlled
 *  onChange   (newValue) => void
 */
const ConfigurationDetailsFields = ({ sections = [], value = {}, onChange, noCard = false }) => {
  const [addedKeys, setAddedKeys] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  const allFields = [...sections]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .flatMap((s) => s.fields ?? []);

  // When value has keys (e.g. loading existing config), show those as added
  useEffect(() => {
    const valueKeys = Object.keys(value || {}).filter((k) => allFields.some((f) => f.key === k));
    if (valueKeys.length > 0 && valueKeys.some((k) => !addedKeys.includes(k))) {
      setAddedKeys((prev) => [...new Set([...prev, ...valueKeys])]);
    }
  }, [value, allFields.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pickerOpen]);

  if (!allFields.length) return null;

  const sectionLabel = sections[0]?.label ?? 'Configuration Details';
  const remaining = allFields.length - addedKeys.length;
  const requiredFields = allFields.filter((f) => f.required);
  const filledRequired = requiredFields.filter((f) => {
    if (!addedKeys.includes(f.key)) return false;
    const v = value[f.key]?.value;
    return v !== undefined && v !== null && v !== '' && v !== false;
  }).length;
  const complete = requiredFields.length > 0 && filledRequired === requiredFields.length;

  const handlePick = (key) => { setAddedKeys((p) => [...p, key]); setPickerOpen(false); };
  const handleDelete = (key) => {
    setAddedKeys((p) => p.filter((k) => k !== key));
    const next = { ...value }; delete next[key]; onChange(next);
  };
  const handleValueChange = (key, v) => onChange({ ...value, [key]: { ...(value[key] ?? {}), value: v } });
  const handleUnitChange  = (key, u) => onChange({ ...value, [key]: { ...(value[key] ?? {}), unit: u  } });

  const content = (
    <>
      {/* Status chip */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: addedKeys.length ? 1.5 : 1 }}>
        {complete
          ? <CheckCircleOutlineIcon sx={{ fontSize: 15, color: '#2e7d32' }} />
          : <TuneIcon sx={{ fontSize: 15, color: '#1565c0' }} />}
        <Chip
          label={addedKeys.length === 0 ? 'No fields added' : complete ? '✓ Required fields filled' : `${filledRequired}/${requiredFields.length} required filled`}
          size="small"
          sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: complete ? 'rgba(46,125,50,0.10)' : 'rgba(21,101,192,0.10)', color: complete ? '#2e7d32' : '#1565c0' }}
        />
      </Box>

      {/* Added rows */}
      {addedKeys.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.25 }}>
          {addedKeys.map((key) => {
            const field = allFields.find((f) => f.key === key);
            if (!field) return null;
            return (
              <AddedFieldRow
                key={key} field={field}
                value={value[key]?.value}
                unit={value[key]?.unit ?? (isAreaField(field) ? 'sqft' : undefined)}
                onValueChange={(v) => handleValueChange(key, v)}
                onUnitChange={(u) => handleUnitChange(key, u)}
                onDelete={() => handleDelete(key)}
              />
            );
          })}
        </Box>
      )}

      {/* Empty state */}
      {addedKeys.length === 0 && (
        <Box sx={{ border: '1.5px dashed #DBEAFE', borderRadius: '8px', py: 2, textAlign: 'center', mb: 1.25, bgcolor: 'rgba(21,101,192,0.02)' }}>
          <Typography sx={{ fontSize: 11.5, color: '#9CA3AF' }}>No fields added yet.</Typography>
          <Typography sx={{ fontSize: 11, color: '#C4C9D4', mt: 0.25 }}>
            Click "+ Add Field" to pick from {allFields.length} available fields.
          </Typography>
        </Box>
      )}

      {/* Add button + dropdown (Menu renders in portal so it is not clipped by drawer/accordion) */}
      {remaining > 0 && (
        <>
          <Box ref={pickerRef} sx={{ display: 'inline-block' }}>
            <DashedAddButton
              onClick={() => setPickerOpen((p) => !p)}
              label={`Add Field (${remaining} left)`}
              color="#1565c0" borderColor="#1565c0" hoverBg="rgba(21,101,192,0.06)"
            />
          </Box>
          <Menu
            anchorEl={pickerRef.current}
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
              paper: {
                sx: {
                  minWidth: 300, maxWidth: 380,
                  minHeight: 120, maxHeight: 320,
                  border: '1px solid #E4E7EC', borderRadius: '9px',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.16)',
                  mt: 0.75,
                },
              },
            }}
            sx={{ zIndex: 1300 }}
          >
            <Box sx={{ px: 1.5, py: 0.9, bgcolor: '#FAFAFA', borderBottom: '1px solid #F0F0F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#6B7280', letterSpacing: '.04em' }}>SELECT FIELD</Typography>
              <Typography sx={{ fontSize: 10.5, color: '#9CA3AF' }}>{remaining} remaining</Typography>
            </Box>
            <MenuList sx={{ py: 0, maxHeight: 280, overflowY: 'auto', display: 'block' }}>
              <FieldPicker fields={allFields} addedKeys={addedKeys} onPick={handlePick} />
            </MenuList>
          </Menu>
        </>
      )}

      {remaining === 0 && addedKeys.length > 0 && (
        <Typography sx={{ fontSize: 11, color: '#9CA3AF', mt: 0.5 }}>✓ All fields added</Typography>
      )}
    </>
  );

  if (noCard) return <Box sx={{ pt: 1 }}>{content}</Box>;
  return (
    <SectionShell
      accentColor={complete ? '#2e7d32' : '#1565c0'}
      icon="🔧"
      title={sectionLabel}
      subtitle={`${addedKeys.length} of ${allFields.length} fields added`}
    >
      {content}
    </SectionShell>
  );
};

export default ConfigurationDetailsFields;
